import React, { useState } from 'react';
import { User, FileText, Briefcase, FileCheck, Wrench, BarChart2, MapPin, Settings, Home, LogOut } from 'lucide-react';
import CustomerManagement from './CustomerManagement';
import ServicesManagement from './ServicesManagement';
import EstimationSystem from './EstimationSystem';
import JobsManagement from './JobsManagement';
import InvoiceManagement from './InvoiceManagement';
import ReportsMenu from './ReportsMenu';
import { Preferences } from './Preferences';
import PropertiesManagement from './PropertiesManagement';
import BusinessDashboard from './BusinessDashboard';
import Login from './Login';
import { useAuth } from './AuthContext';

// Shared header for all non-dashboard pages
function PageHeader({ onBack, onSignOut, displayName, roleLabel, extraClass = '' }) {
  return (
    <div className={`bg-white shadow-sm border-b ${extraClass}`}>
      <div className="px-6 py-4">
        <div className="flex justify-between items-center">
          <img src="/jft-logo.png" alt="JFT Contracting" className="h-8 w-auto" />
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500 hidden sm:block">
              {displayName}{roleLabel ? ` · ${roleLabel}` : ''}
            </span>
            <button
              onClick={onSignOut}
              className="flex items-center gap-1 text-sm text-gray-400 hover:text-red-600 transition-colors"
              title="Sign out"
            >
              <LogOut size={14} />
              <span className="hidden sm:inline">Sign out</span>
            </button>
            <button
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors duration-200 shadow-md hover:shadow-lg text-sm"
              onClick={onBack}
            >
              ← BACK
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function App() {
  const { session, loading, signOut, user, profile } = useAuth();
  const [activePage, setActivePage] = useState('dashboard');
  const [conversionData, setConversionData] = useState(null);
  const [customerFilter, setCustomerFilter] = useState(null);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-500 text-lg">Loading...</div>
      </div>
    );
  }

  if (!session) {
    return <Login />;
  }

  const displayName = profile?.full_name || user?.email || 'User';
  const roleLabel = profile?.role ? profile.role.charAt(0).toUpperCase() + profile.role.slice(1) : '';

  const navTiles = [
    { id: 'home', title: 'HOME', icon: Home, color: 'bg-blue-500' },
    { id: 'customers', title: 'CUSTOMERS', icon: User, color: 'bg-blue-500' },
    { id: 'estimating', title: 'ESTIMATING', icon: FileText, color: 'bg-green-500' },
    { id: 'jobs', title: 'JOBS', icon: Briefcase, color: 'bg-pink-500' },
    { id: 'invoicing', title: 'INVOICING', icon: FileCheck, color: 'bg-yellow-500' },
    { id: 'services', title: 'SERVICES', icon: Wrench, color: 'bg-pink-500' },
    { id: 'reporting', title: 'REPORTING', icon: BarChart2, color: 'bg-gray-700' },
    { id: 'properties', title: 'PROPERTIES', icon: MapPin, color: 'bg-orange-500' },
    { id: 'preferences', title: 'PREFERENCES', icon: Settings, color: 'bg-gray-600' }
  ];

  const headerProps = {
    onBack: () => setActivePage('dashboard'),
    onSignOut: signOut,
    displayName,
    roleLabel,
  };

  if (activePage === 'home') {
    return (
      <div className="min-h-screen bg-gray-100">
        <PageHeader {...headerProps} />
        <div className="px-6 py-8"><BusinessDashboard /></div>
      </div>
    );
  }

  if (activePage === 'customers') {
    return (
      <div className="min-h-screen bg-gray-100">
        <PageHeader {...headerProps} />
        <div className="px-6 py-8"><CustomerManagement onNavigate={(page, customer) => { setCustomerFilter(customer || null); setActivePage(page); }} /></div>
      </div>
    );
  }

  if (activePage === 'services') {
    return (
      <div className="min-h-screen bg-gray-100">
        <PageHeader {...headerProps} />
        <div className="px-6 py-8"><ServicesManagement /></div>
      </div>
    );
  }

  if (activePage === 'estimating') {
    return (
      <div className="min-h-screen bg-gray-100">
        <PageHeader {...headerProps} />
        <div className="px-6 py-8">
          <EstimationSystem
            onConvertToJob={(estimateData) => {
              setConversionData(estimateData);
              setActivePage('jobs');
            }}
            initialSearchTerm={customerFilter ? `${customerFilter.first_name} ${customerFilter.last_name}` : ''}
          />
        </div>
      </div>
    );
  }

  if (activePage === 'jobs') {
    return (
      <div className="min-h-screen bg-gray-100">
        <PageHeader
          {...headerProps}
          onBack={() => { setConversionData(null); setActivePage('dashboard'); }}
        />
        <div className="px-6 py-8">
          <JobsManagement
            conversionData={conversionData}
            onConversionComplete={() => setConversionData(null)}
            initialSearchTerm={customerFilter ? `${customerFilter.first_name} ${customerFilter.last_name}` : ''}
          />
        </div>
      </div>
    );
  }

  if (activePage === 'invoicing') {
    return (
      <div className="min-h-screen bg-gray-100">
        <PageHeader {...headerProps} />
        <div className="px-6 py-8"><InvoiceManagement initialSearchTerm={customerFilter ? `${customerFilter.first_name} ${customerFilter.last_name}` : ''} /></div>
      </div>
    );
  }

  if (activePage === 'reporting') {
    return (
      <div className="min-h-screen bg-gray-100">
        <PageHeader {...headerProps} extraClass="no-print" />
        <ReportsMenu />
      </div>
    );
  }

  if (activePage === 'properties') {
    return (
      <div className="min-h-screen bg-gray-100">
        <PageHeader {...headerProps} />
        <div className="px-6 py-8"><PropertiesManagement /></div>
      </div>
    );
  }

  if (activePage === 'preferences') {
    return (
      <div className="min-h-screen bg-gray-100">
        <PageHeader {...headerProps} />
        <Preferences />
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow-sm border-b">
        <div className="px-6 py-8">
          <div className="relative text-center">
            <div className="flex justify-center mb-4">
              <img src="/jft-logo.png" alt="JFT Contracting" className="h-20 w-auto" />
            </div>
            <div className="w-32 h-1 bg-red-800 mx-auto mb-4"></div>
            <h2 className="text-2xl font-semibold text-gray-600 tracking-wide">
              BUSINESS MANAGEMENT SYSTEM
            </h2>
            <div className="absolute top-0 right-0 flex flex-col items-end">
              <span className="text-sm text-gray-500 mb-1">
                {displayName}{roleLabel ? ` · ${roleLabel}` : ''}
              </span>
              <button
                onClick={signOut}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-600 transition-colors"
              >
                <LogOut size={14} />
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-12">
        <div className="grid grid-cols-3 gap-6 max-w-5xl mx-auto">
          {navTiles.map(tile => {
            const IconComponent = tile.icon;
            return (
              <div
                key={tile.id}
                className={`${tile.color} text-white rounded-lg shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 cursor-pointer group`}
                onClick={() => { setCustomerFilter(null); setActivePage(tile.id); }}
              >
                <div className="h-32 flex flex-col items-center justify-center p-6">
                  <div className="mb-3 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent size={40} strokeWidth={1.5} />
                  </div>
                  <div className="text-center font-bold text-base tracking-wide leading-tight">
                    {tile.title}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default App;
